"use client";

import React, { FC } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useWindowSize } from "react-use";
import Confetti from "react-confetti";

interface FeedbackModalProps {
  children: React.ReactNode;
  handleContinue: () => void;
  type?: 'success' | 'error' | 'info';
  showConfetti?: boolean;
}

export const FeedbackModal: FC<FeedbackModalProps> = ({
  children,
  handleContinue,
  type = 'success',
  showConfetti = false,
}) => {
  const { width, height } = useWindowSize();
  
  const getButtonColors = () => {
    switch (type) {
      case 'success':
        return 'from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700';
      case 'error':
        return 'from-red-600 to-brand-amber hover:from-red-700 hover:to-orange-700';
      case 'info':
        return 'from-blue-600 to-brand-slate hover:from-blue-700 hover:to-brand-slate';
      default:
        return 'from-brand-teal to-blue-600 hover:from-brand-slate hover:to-blue-700';
    }
  };

  return (
    <>
      {showConfetti && <Confetti width={width} height={height} />}
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
          onClick={handleContinue}
        >
          <motion.div
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.5, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className={`bg-gradient-to-br from-brand-navy/90 to-blue-900/90 backdrop-blur-xl rounded-3xl p-8 border border-white/20 max-w-md w-full mx-4 text-center`}
            onClick={(e) => e.stopPropagation()}
          >
            {children}
            <motion.button
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleContinue}
              className={`bg-gradient-to-r ${getButtonColors()} text-white px-8 py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg mt-4 cursor-pointer`}
            >
              Continue
            </motion.button>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    </>
  );
};